import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { RiderLayout } from "@/components/rider/RiderLayout";
import { getEventById, getRiderQuote } from "@/services/riderService";
import { CommunityEvent, QuoteRequest } from "@/types/rider";
import { Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { AnimatedItinerary } from "@/components/rider/quote/AnimatedItinerary";
import { parseISO, format, addHours, parse, subHours } from "date-fns";

const RiderQuote = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryParams = new URLSearchParams(location.search);
  
  // Scroll to top on mobile when page loads
  useEffect(() => {
    if (window.innerWidth < 768) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, []);
  
  // Get referrer to know where to navigate back to
  const referrer = document.referrer;
  const returnPathParam = queryParams.get("returnPath");
  const isFromEventSelect = referrer.includes("rider-event-select") || referrer.includes("we-belong-here-miami") || queryParams.has("eventId");
  
  const eventId = queryParams.get("eventId");
  const destination = queryParams.get("destination");
  const placeId = queryParams.get("placeId");
  const capacityParam = queryParams.get("capacity");
  const durationParam = queryParams.get("duration");
  const isRoundTripParam = queryParams.get("isRoundTrip");
  const dateParam = queryParams.get("date");
  const startTimeParam = queryParams.get("startTime");
  const priceParam = queryParams.get("price");
  const addOnsParam = queryParams.get("addOns");
  const notesParam = queryParams.get("notes");
  const returnDestinationParam = queryParams.get("returnDestination");
  const returnPickupParam = queryParams.get("returnPickup");
  
  const [selectedEvent, setSelectedEvent] = useState<CommunityEvent | null>(null);
  const [loading, setLoading] = useState(eventId ? true : false);
  const [quoteId, setQuoteId] = useState<string | null>(null);
  const [noOrganizationsInArea, setNoOrganizationsInArea] = useState<boolean>(false); // Add state for no organizations flag
  const [timezoneAbbreviation, setTimezoneAbbreviation] = useState<string | undefined>(undefined);
  const [timezoneOffset, setTimezoneOffset] = useState<string | undefined>(undefined);
  
  // Form state
  const [phoneNumber, setPhoneNumber] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState(destination || "");
  const [capacity, setCapacity] = useState(capacityParam ? parseInt(capacityParam) : 10);
  const [duration, setDuration] = useState(durationParam ? parseInt(durationParam) : 3);
  const [isRoundTrip, setIsRoundTrip] = useState(isRoundTripParam === "true");
  const [tripDate, setTripDate] = useState<Date | null>(dateParam ? parseISO(dateParam) : new Date());
  const [price, setPrice] = useState<number>(priceParam ? parseInt(priceParam) : 1200);
  const [promoCode, setPromoCode] = useState<string>("");
  const [selectedAddOns, setSelectedAddOns] = useState<any[]>([]);

  // Return trip state
  const [returnFrom, setReturnFrom] = useState(returnPickupParam || "");
  const [returnTo, setReturnTo] = useState(returnDestinationParam || "");

  // Initialize start/end times based on source
  const [startTime, setStartTime] = useState<string>("");
  const [endTime, setEndTime] = useState<string>("");
  const [eventStartTime, setEventStartTime] = useState<string>("");
  
  // Calculate end time based on start time and duration
  const calculateEndTime = (startTimeStr: string, durationHours: number): string => {
    if (!startTimeStr) return "";
    
    try {
      // Parse the start time and add the duration
      const [hours, minutes] = startTimeStr.split(':').map(Number);
      const startDateTime = new Date();
      startDateTime.setHours(hours, minutes, 0);
      const endDateTime = addHours(startDateTime, durationHours);
      
      return format(endDateTime, "HH:mm");
    } catch (error) {
      console.error("Error calculating end time:", error);
      return "";
    }
  };
  
  // Calculate start time by subtracting duration from end time
  const calculateStartTime = (endTimeStr: string, durationHours: number): string => {
    if (!endTimeStr) return "";
    
    try {
      // Parse the end time and subtract the duration
      const [hours, minutes] = endTimeStr.split(':').map(Number);
      const endDateTime = new Date();
      endDateTime.setHours(hours, minutes, 0);
      const startDateTime = subHours(endDateTime, durationHours);
      
      return format(startDateTime, "HH:mm");
    } catch (error) {
      console.error("Error calculating start time:", error);
      return "";
    }
  };
  
  // Format date for display
  const getFormattedDate = (): string => {
    if (!tripDate) return "";
    return format(tripDate, "EEEE, MMM-dd").toUpperCase();
  };

  const formattedDate = getFormattedDate();
  
  // Handle user updating the start time
  const handleStartTimeChange = (timeString: string) => {
    setStartTime(timeString);
    setEndTime(calculateEndTime(timeString, duration));
  };
  
  // Handle user updating the end time
  const handleEndTimeChange = (timeString: string) => {
    setEndTime(timeString);
    setStartTime(calculateStartTime(timeString, duration));
  };
  
  // Handle navigation back to the previous screen
  const handleBackNavigation = () => {
    if (returnPathParam) {
      navigate(returnPathParam);
    } else if (isFromEventSelect) {
      navigate('/rider-event-select');
    } else {
      navigate('/');
      // Scroll to top on mobile when going back to landing page
      if (window.innerWidth < 768) {
        setTimeout(() => {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }, 100);
      }
    }
  };
  
  // Convert 12-hour format time to 24-hour format
  const convertTo24Hour = (time12h: string): string => {
    if (!time12h?.trim()) return "";
    
    // If it's already 24-hour (no AM/PM), normalize and return
    const match24 = time12h.match(/^\s*(\d{1,2}):(\d{2})\s*$/);
    if (match24 && !/am|pm/i.test(time12h)) {
      const h = Math.min(23, Math.max(0, parseInt(match24[1], 10)));
      const m = match24[2];
      return `${h.toString().padStart(2, "0")}:${m}`;
    }
    
    // If it's 12-hour with AM/PM, convert to 24-hour
    const match12 = time12h.match(/^\s*(\d{1,2}):(\d{2})\s*(AM|PM)\s*$/i);
    if (match12) {
      let h = parseInt(match12[1], 10);
      const m = match12[2];
      const p = match12[3].toUpperCase();
      if (p === "PM" && h < 12) h += 12;
      if (p === "AM" && h === 12) h = 0;
      return `${h.toString().padStart(2, "0")}:${m}`;
    }
    
    console.warn("Unexpected time format:", time12h);
    return time12h; // Never default to noon
  };
  
  // Load event data if eventId is provided
  useEffect(() => {
    const fetchEventDetails = async () => {
      if (eventId) {
        try {
          const event = await getEventById(eventId);
          if (event) {
            setSelectedEvent(event);
            // Leave from empty so placeholder "Enter Your Pickup Location" shows
            setTo(event.name);
            
            // Set timezone from event
            if (event.timezoneOffset) {
              setTimezoneOffset(event.timezoneOffset);
            }
            if (event.timezoneAbbreviation) {
              setTimezoneAbbreviation(event.timezoneAbbreviation);
            }
            
            // Parse and set the trip date from event starts_at
            if (event.date) {
              setTripDate(parseISO(event.date));
            }
            
            // If this is an event, set the event start time (simulating DB fetch)
            // In a real implementation, this would come from the event data
            setEventStartTime("19:00"); // Example: 7:00 PM event start time
            
            // Calculate pickup time by subtracting duration from event start time
            const calculatedPickupTime = calculateStartTime("19:00", duration);
            setStartTime(calculatedPickupTime);
            setEndTime("19:00"); // Event start time is the dropoff time
          }
          setLoading(false);
        } catch (error) {
          console.error("Failed to fetch event details:", error);
          setLoading(false);
        }
      }
    };

    fetchEventDetails();
  }, [eventId, duration]);

  // Parse add-ons from URL parameters
  useEffect(() => {
    if (addOnsParam) {
      try {
        const parsedAddOns = JSON.parse(addOnsParam);
        setSelectedAddOns(parsedAddOns);
      } catch (error) {
        console.error("Failed to parse add-ons:", error);
      }
    }
  }, [addOnsParam]);

  // Get quote ID when component mounts if we have the required data
  useEffect(() => {
    const fetchQuoteId = async () => {
      if (destination && !quoteId && !eventId) {
        try {
          const quoteRequest: QuoteRequest = {
            phoneNumber: "",
            from: from || "",
            to: destination,
            isRoundTrip: isRoundTripParam === "true",
            capacity: capacityParam ? parseInt(capacityParam) : 10,
            duration: durationParam ? parseInt(durationParam) : 3,
            date: dateParam ? parseISO(dateParam) : new Date()
          };

          const quoteResponse = await getRiderQuote(quoteRequest);
          setQuoteId(quoteResponse.quoteId);
          setNoOrganizationsInArea(quoteResponse.noOrganizationsInArea || false); // Set the flag
          setTimezoneAbbreviation(quoteResponse.timezoneAbbreviation);
          setTimezoneOffset(quoteResponse.timezoneOffset);
        } catch (error) {
          console.error("Failed to get quote ID:", error);
        }
      }
    };

    fetchQuoteId();
  }, [destination, quoteId, eventId, from, isRoundTripParam, capacityParam, durationParam, dateParam]);
  
  const handlePayment = async () => {
    // Basic form validation
    if (!phoneNumber || phoneNumber.length < 10) {
      toast({
        title: "Invalid phone number",
        description: "Please enter a valid 10-digit phone number",
        variant: "destructive"
      });
      return;
    }
    
    if (!from) {
      toast({
        title: "Missing pickup location",
        description: "Please enter where you're traveling from",
        variant: "destructive"
      });
      return;
    }
    
    if (!to) {
      toast({
        title: "Missing destination",
        description: "Please enter where you're traveling to",
        variant: "destructive"
      });
      return;
    }

    // In a real implementation, you would include the promoCode in the API request
    // For example:
    // const tripData = {
    //   ...otherTripData,
    //   promo_code: promoCode
    // };
    // await createTrip(tripData);

    // In a real implementation, you would handle Apple Pay here
    // For now, we'll just show a toast message
    toast({
      title: "Processing Apple Pay",
      description: "Your Apple Pay transaction is being processed",
    });
    
    // Simulate a successful payment
    setTimeout(() => {
      toast({
        title: "Booking confirmed!",
        description: "Your party bus has been booked successfully",
      });
    }, 2000);
  };

  if (loading) {
    return (
      <RiderLayout>
        <div className="flex flex-col items-center justify-center py-12">
          <Loader2 className="h-12 w-12 text-neon-green animate-spin mb-4" />
          <p className="text-black">Loading event details...</p>
        </div>
      </RiderLayout>
    );
  }

  return (
    <RiderLayout>
      <div className="min-h-screen">
        <AnimatedItinerary
          eventName={selectedEvent?.name || to || "Your Destination"}
          price={price}
          from={from}
          to={to}
          setFrom={setFrom}
          setTo={setTo}
          phoneNumber={phoneNumber}
          handlePhoneNumberChange={(e) => setPhoneNumber(e.target.value)}
          handlePayment={handlePayment}
          selectedEvent={selectedEvent}
          numPeople={capacity}
          duration={duration}
          isRoundTrip={isRoundTrip}
          tripDate={tripDate}
          placeId={placeId}
          quoteId={quoteId}
          noOrganizationsInArea={noOrganizationsInArea}
          timezoneAbbreviation={timezoneAbbreviation}
          timezoneOffset={timezoneOffset}
          formattedDate={formattedDate}
          startTime={startTime}
          endTime={endTime}
          handleStartTimeChange={handleStartTimeChange}
          handleEndTimeChange={handleEndTimeChange}
          handleBackNavigation={handleBackNavigation}
          convertTo24Hour={convertTo24Hour}
          isFromEventSelect={isFromEventSelect}
          initialNotes={notesParam || ""}
          returnFrom={returnFrom}
          returnTo={returnTo}
          setReturnFrom={setReturnFrom}
          setReturnTo={setReturnTo}
        />
      </div>
    </RiderLayout>
  );
};

export default RiderQuote;
